/**
 * Notification sound service.
 *
 * Plays a short, calm sound when a new notification arrives. Designed for the
 * web app (desktop + mobile browser); it stays silent on the native mobile app,
 * which already gets a system push sound.
 *
 * Responsibilities:
 *  - own a single, reusable, preloaded <audio> element
 *  - work around the browser autoplay policy (programmatic play() is blocked
 *    until the page has seen a user gesture) via a one-time "unlock"
 *  - throttle bursts so many notifications at once make ONE sound, not a stream
 *  - resolve which sound to play from the user's saved preference
 *
 * All preferences are stored per-user on userProfile (sync across devices):
 *   - prefs_notificationSoundEnabled    master on/off — OFF by default (opt-in)
 *   - prefs_notificationSoundId         e.g. 'soft-chime' | 'soft-pop' | 'marimba' | 'custom'
 *   - prefs_notificationSoundCustomUrl  Storage URL, used when id === 'custom'
 */
import _ from 'lodash'
import { Capacitor } from '@capacitor/core'

// Bundled, royalty-free presets (synthesized — see scripts/generate-notification-sounds.js).
// Served from public/, referenced via BASE_URL (there is no webpack audio loader).
const BASE = process.env.BASE_URL || '/'
export const SOUND_PRESETS = [
  { id: 'soft-chime', label: 'Soft Chime', url: BASE + 'sounds/soft-chime.wav' },
  { id: 'soft-pop', label: 'Soft Pop', url: BASE + 'sounds/soft-pop.wav' },
  { id: 'marimba', label: 'Marimba', url: BASE + 'sounds/marimba.wav' },
  { id: 'block-wood', label: 'Wood', url: BASE + 'sounds/block-wood.wav' },
  { id: 'block-stone', label: 'Stone', url: BASE + 'sounds/block-stone.wav' },
  { id: 'ping', label: 'Ping', url: BASE + 'sounds/ping.wav' },
  { id: 'ding', label: 'Ding', url: BASE + 'sounds/ding.wav' },
  { id: 'bim', label: 'Bim', url: BASE + 'sounds/bim.wav' },
  { id: 'klick', label: 'Klick', url: BASE + 'sounds/klick.wav' },
  { id: 'two-note', label: 'Two Note', url: BASE + 'sounds/two-note.mp3' },
  { id: 'tone', label: 'Tone', url: BASE + 'sounds/tone.wav' },
  { id: 'message', label: 'Message', url: BASE + 'sounds/message.wav' },
  { id: 'mobile', label: 'Mobile', url: BASE + 'sounds/mobile.wav' },
  { id: 'gasp', label: 'Gasp', url: BASE + 'sounds/gasp.wav' },
  { id: 'place', label: 'Place', url: BASE + 'sounds/place.wav' },
  { id: 'notification', label: 'Alert', url: BASE + 'sounds/notification.mp3' },
  { id: 'drop', label: 'Drop', url: BASE + 'sounds/drop.wav' },
  { id: 'sparkle', label: 'Sparkle', url: BASE + 'sounds/sparkle.wav' },
  { id: 'chirp', label: 'Chirp', url: BASE + 'sounds/chirp.wav' },
]
export const DEFAULT_PRESET_ID = 'notification'
export const CUSTOM_ID = 'custom'
export const DEFAULT_VOLUME = 0.7
const THROTTLE_MS = 4000

export function resolveVolume(userProfile) {
  const v = userProfile && userProfile.prefs_notificationSoundVolume
  return typeof v === 'number' && v >= 0 && v <= 1 ? v : DEFAULT_VOLUME
}

// --- resolving the source URL from saved prefs ------------------------------

export function resolveSrc(userProfile) {
  const p = userProfile || {}
  if (p.prefs_notificationSoundId === CUSTOM_ID && p.prefs_notificationSoundCustomUrl) {
    return p.prefs_notificationSoundCustomUrl
  }
  const preset =
    SOUND_PRESETS.find(s => s.id === p.prefs_notificationSoundId) ||
    SOUND_PRESETS.find(s => s.id === DEFAULT_PRESET_ID)
  return preset.url
}

// --- the single audio element -----------------------------------------------

let audioEl = null
let currentSrc = null

function getAudio() {
  if (!audioEl) {
    audioEl = new Audio()
    audioEl.preload = 'auto'
  }
  return audioEl
}

function setSrc(a, src) {
  if (currentSrc !== src) {
    a.src = src
    currentSrc = src
  }
}

// --- autoplay unlock --------------------------------------------------------
// Chromium (incl. the nativefier-wrapped build) rejects audio.play() with
// NotAllowedError until the page has had a user gesture. We "bless" the element
// by playing it muted inside the first click/keydown, so later notification-
// triggered plays (which have no gesture) are allowed.

let unlockRegistered = false

export function registerUnlock() {
  if (unlockRegistered || typeof document === 'undefined') return
  unlockRegistered = true
  const events = ['click', 'keydown', 'touchstart']
  const unlock = () => {
    events.forEach(evt => document.removeEventListener(evt, unlock))
    const a = getAudio()
    setSrc(a, resolveSrc(null)) // default preset is enough to bless the element
    a.muted = true
    const done = () => {
      try {
        a.pause()
        a.currentTime = 0
      } catch (e) {
        /* ignore */
      }
      a.muted = false
    }
    try {
      const p = a.play()
      if (p && p.then) p.then(done).catch(() => { a.muted = false })
      else done()
    } catch (e) {
      a.muted = false
    }
  }
  events.forEach(evt => document.addEventListener(evt, unlock, { once: true, passive: true }))
}

// --- playback ----------------------------------------------------------------

function doPlay(userProfile) {
  const a = getAudio()
  setSrc(a, resolveSrc(userProfile))
  a.volume = resolveVolume(userProfile)
  a.muted = false
  try {
    a.currentTime = 0
  } catch (e) {
    /* ignore (not yet loaded) */
  }
  const p = a.play()
  // Swallow autoplay-blocked / load errors. The production build strips
  // console.log, so logging here would be invisible anyway.
  if (p && p.catch) p.catch(() => {})
}

// Leading-edge, no trailing: the first arrival in a quiet window plays instantly;
// further arrivals within the window are dropped (no delayed echo). Firestore
// already batches a burst into one snapshot, and notifyArrival is called once
// per snapshot — the throttle collapses multiple back-to-back snapshots too.
const throttledPlay = _.throttle(doPlay, THROTTLE_MS, { leading: true, trailing: false })

/**
 * Called once per Firestore snapshot that contains a genuinely-new notification.
 * Gated to non-native (web) + the per-device enable flag, then throttled.
 */
export function notifyArrival(app) {
  if (Capacitor.isNativePlatform()) return // native mobile already plays a push sound
  // Off by default; the user opts in per-account via prefs_notificationSoundEnabled.
  if (!app || !app.userProfile || !app.userProfile.prefs_notificationSoundEnabled) return
  throttledPlay(app.userProfile)
}

/**
 * Play a sound immediately for the settings preview. Bypasses the throttle and
 * the enable/native gates — the click that triggers it is itself a user gesture
 * (which also unlocks autoplay).
 */
export function preview(soundId, customUrl, volume) {
  const a = getAudio()
  let src
  if (soundId === CUSTOM_ID) src = customUrl
  else {
    const preset =
      SOUND_PRESETS.find(s => s.id === soundId) ||
      SOUND_PRESETS.find(s => s.id === DEFAULT_PRESET_ID)
    src = preset.url
  }
  if (!src) return
  setSrc(a, src)
  a.volume = typeof volume === 'number' ? volume : DEFAULT_VOLUME
  a.muted = false
  try {
    a.currentTime = 0
  } catch (e) {
    /* ignore */
  }
  const p = a.play()
  if (p && p.catch) p.catch(() => {})
}

export default {
  SOUND_PRESETS,
  DEFAULT_PRESET_ID,
  CUSTOM_ID,
  DEFAULT_VOLUME,
  resolveSrc,
  resolveVolume,
  registerUnlock,
  notifyArrival,
  preview,
}
